import type { DateCellData } from './types';

import {
  isCustomCellData,
  getCustomCellTemplate,
  isKindOfCustomCell,
} from '../utils';

import { kind } from './types';

import { DateEnGB, DateEnUS, DateEnCA, DEFAULT_LANG } from './constants';

const isFutureDate = (date: Date, now: Date = new Date()) =>
  date.getTime() > now.getTime();

const baseDateBoundary = new Date(1900, 0, 1);

const isAncientDate = (date: Date) =>
  date.getTime() < baseDateBoundary.getTime();

const isDate = (date: Date | null): date is Date =>
  date !== null && !isNaN(date.valueOf());

export const getFomattedDateString = (
  date: Date | null,
  lang: DateCellData['lang'] = DEFAULT_LANG
) => {
  if (date === null) return '';
  if (lang === 'en-US') return DateEnUS.format(date);
  if (lang === 'en-CA') return DateEnCA.format(date);
  return DateEnGB.format(date);
};

export const getDefaultCellData = ({
  value = null,
  error = null,
  lang = DEFAULT_LANG,
}: Partial<DateCellData> = {}): DateCellData => ({
  kind,
  lang,
  value,
  error,
});

const isCellValue = (value: unknown): value is DateCellData['value'] => {
  if (value instanceof Date && !isNaN(value.valueOf())) return true;
  if (value === null) return true;
  return false;
};

const isCellData = (data: unknown): data is DateCellData => {
  if (!isCustomCellData(data)) return false;
  return data.kind === kind;
};

type Args = Pick<Partial<DateCellData>, 'error' | 'lang'> & {
  value?: unknown;
};

/**
 * ## getCellTemplate
 *
 * @param value The value of the DateCell
 * @returns A custom DateCell object and its data
 */
export const getCellTemplate = ({ value = null, error, lang }: Args = {}) => {
  if (isCellValue(value)) {
    const cellData = getDefaultCellData({ value, error, lang });
    const copyData = isDate(value) ? getFomattedDateString(value, lang) : '';

    return getCustomCellTemplate({ cellData, copyData });
  }
  return getCustomCellTemplate({ cellData: getDefaultCellData({ lang }) });
};

export const isCell = (
  cell: unknown
): cell is ReturnType<typeof getCellTemplate> => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  if (isKindOfCustomCell(cell)) {
    return isCellData(cell.data);
  }
  return false;
};

export const getCellData = (data?: unknown) => {
  if (isCellData(data) && isCellValue(data.value)) {
    return data;
  }

  return getDefaultCellData();
};

export const validateDate = (date: Date | null): string | null => {
  if (!isDate(date)) {
    return 'Date not valid';
  }

  if (isFutureDate(date)) {
    return `Date can't be in the future`;
  }

  if (isAncientDate(date)) {
    return `Date can't be before 1900`;
  }

  return null;
};

// en-GB
const ddmmyyyy = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
const ddmmyy = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{2}$/;
const dmyyyy = /^([1-9]|[12][0-9]|3[01])\/([1-9]|1[0-2])\/\d{4}$/;
const dmyy = /^([1-9]|[12][0-9]|3[01])\/([1-9]|1[0-2])\/\d{2}$/;
// en-US
const mmddyyyy = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
const mmddyy = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{2}$/;
const mdyyyy = /^([1-9]|1[0-2])\/([1-9]|[12][0-9]|3[01])\/\d{4}$/;
const mdyy = /^([1-9]|1[0-2])\/([1-9]|[12][0-9]|3[01])\/\d{2}$/;
// en-CA
const yyyymmdd = /^\d{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
const yymmdd = /^\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
const yyyymd = /^\d{4}\/([1-9]|1[0-2])\/([1-9]|[12][0-9]|3[01])$/;
const yymd = /^\d{2}\/([1-9]|1[0-2])\/([1-9]|[12][0-9]|3[01])$/;

export const validateFormat = (value: string, lang: DateCellData['lang']) => {
  if (lang === 'en-US') {
    if (mmddyyyy.test(value)) return null;
    if (mmddyy.test(value)) return null;
    if (mdyyyy.test(value)) return null;
    if (mdyy.test(value)) return null;
  }

  if (lang === 'en-GB') {
    if (ddmmyyyy.test(value)) return null;
    if (ddmmyy.test(value)) return null;
    if (dmyyyy.test(value)) return null;
    if (dmyy.test(value)) return null;
  }

  if (lang === 'en-CA') {
    if (yyyymmdd.test(value)) return null;
    if (yymmdd.test(value)) return null;
    if (yyyymd.test(value)) return null;
    if (yymd.test(value)) return null;
  }

  return 'Wrong date format';
};

// We normalise the date string to 'MM/DD/YYYY' to create a valid new Date object
const normaliseDate = (value: string, lang: DateCellData['lang']) => {
  if (lang === 'en-GB') {
    const [day, month, year] = value.split('/');
    return `${month}/${day}/${year}`;
  }
  if (lang === 'en-CA') {
    const [year, month, day] = value.split('/');
    return `${month}/${day}/${year}`;
  }
  return value;
};

export const validatePastedValue = (
  pastedValue: string,
  cell: DateCellData
): DateCellData => {
  const { lang } = cell;

  const santiasedValue = pastedValue.replace(/\s/g, '').replace(/\.|-/g, '/');

  let error = validateFormat(santiasedValue, lang);

  if (error !== null) {
    return {
      ...cell,
      value: null,
      error,
    };
  }

  const normalisedValue = normaliseDate(santiasedValue, lang);

  const date = new Date(normalisedValue);

  error = validateDate(date);

  if (error !== null) {
    return {
      ...cell,
      value: null,
      error,
    };
  }

  return {
    ...cell,
    value: date,
    error: null,
  };
};
